import React from 'react';
import { Box, Unstable_Grid2 as Grid, useTheme } from '@mui/material';

import { Button, Text, TextField } from 'components';
import { useToast } from 'core/toast/hooks';
import { useHasMobileView } from 'hooks/useHasMobileView';
import { useTranslations } from 'hooks/useTranslations';

import { UseMutation, UseQuery } from 'useQuery';

interface OrganisationUsageProps {
  organisationId: string;
}

function OrganisationUsage({ organisationId }: OrganisationUsageProps) {
  const theme = useTheme();
  const { hasMobileView } = useHasMobileView();
  const translations = useTranslations('organisationUsage');
  const { addToast } = useToast();

  const {
    data: usageData,
    refetch: refetchUsage,
    isLoading: loadingUsage,
  } = UseQuery.organisationUsage({ id: organisationId });

  const { mutateAsync: mutateAsyncUpdateUsageFeeConfig, isLoading: loadingUpdate } =
    UseMutation.updateOrganisationUsageFeeConfig();

  const [localFees, setLocalFees] = React.useState<Record<string, number>>({});
  const [localStartingFrom, setLocalStartingFrom] = React.useState<Record<string, number>>({});

  React.useEffect(() => {
    if (usageData?.result?.metrics) {
      const fees: Record<string, number> = {};
      const startingFrom: Record<string, number> = {};
      usageData.result.metrics.forEach((metric) => {
        fees[metric.fee_type] = metric.fee_amount;
        startingFrom[metric.fee_type] = metric.starting_from;
      });
      setLocalFees(fees);
      setLocalStartingFrom(startingFrom);
    }
  }, [usageData]);

  const hasChanges = React.useMemo(() => {
    if (!usageData?.result?.metrics) {
      return false;
    }
    return usageData.result.metrics.some(
      (metric) =>
        localFees[metric.fee_type] !== metric.fee_amount ||
        localStartingFrom[metric.fee_type] !== metric.starting_from,
    );
  }, [localFees, localStartingFrom, usageData]);

  function handleFeeChange(feeType: string, value: number) {
    setLocalFees((prev) => ({
      ...prev,
      [feeType]: Math.round(value * 100),
    }));
  }

  function handleStartingFromChange(feeType: string, value: number) {
    setLocalStartingFrom((prev) => ({
      ...prev,
      [feeType]: Math.round(value),
    }));
  }

  async function save() {
    const fees = Object.entries(localFees).map(([fee_type, fee_amount]) => ({
      fee_type,
      fee_amount,
      starting_from: localStartingFrom[fee_type] ?? 0,
    }));

    try {
      await mutateAsyncUpdateUsageFeeConfig({ id: organisationId, fees });
      await refetchUsage();
      addToast({ title: translations.updatedSuccessfully, severity: 'success' });
    } catch (error) {
      addToast({ error, severity: 'error' });
    }
  }

  function formatEuro(cents: number | undefined) {
    if (cents === undefined || isNaN(cents)) {
      return '€ -,--';
    }
    return `€ ${(cents / 100).toFixed(2).replace('.', ',')}`;
  }

  function feeTypeLabel(feeType: string) {
    return (translations as Record<string, string>)[feeType] ?? feeType;
  }

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          padding: 2,
          marginBottom: 2,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          maxWidth: `calc(100vw - ${theme.spacing(hasMobileView ? 4 : 6)})`,
        }}
      >
        <Box sx={{ paddingBottom: 2 }}>
          <Text.LargeBold>{translations.title}</Text.LargeBold>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
            paddingY: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ minWidth: 200 }}>
            <Text.Bold>{translations.feeType}</Text.Bold>
          </Box>
          <Box sx={{ minWidth: 100 }}>
            <Text.Bold>{translations.count}</Text.Bold>
          </Box>
          <Box sx={{ width: 150, flexShrink: 0 }}>
            <Text.Bold>{translations.feeAmount}</Text.Bold>
          </Box>
          <Box sx={{ width: 150, flexShrink: 0 }}>
            <Text.Bold>{translations.startingFrom}</Text.Bold>
          </Box>
          <Box sx={{ minWidth: 120 }}>
            <Text.Bold>{translations.total}</Text.Bold>
          </Box>
        </Box>

        {usageData?.result?.metrics.map((metric) => (
          <Box
            key={metric.fee_type}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
              paddingY: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ minWidth: 200 }}>
              <Text>{feeTypeLabel(metric.fee_type)}</Text>
            </Box>
            <Box sx={{ minWidth: 100 }}>
              <Text>{metric.count}</Text>
            </Box>
            <Box sx={{ width: 150, flexShrink: 0 }}>
              <TextField
                type="number"
                label=""
                value={(localFees[metric.fee_type] ?? 0) / 100}
                onChange={(value) => handleFeeChange(metric.fee_type, value)}
                fullWidth
                adornment="EUR"
              />
            </Box>
            <Box sx={{ width: 150, flexShrink: 0 }}>
              <TextField
                type="number"
                label=""
                value={localStartingFrom[metric.fee_type] ?? 0}
                onChange={(value) => handleStartingFromChange(metric.fee_type, value)}
                fullWidth
              />
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Text>{formatEuro(metric.total_cents)}</Text>
            </Box>
          </Box>
        ))}

        <Box sx={{ paddingTop: 2 }}>
          <Text.Small color="error.main">{translations.invoiceNote}</Text.Small>
          <Box sx={{ paddingTop: 1 }}>
            <Text.Bold>
              {`${translations.totalUsageFee}: ${formatEuro(
                usageData?.result?.total_usage_fee_cents,
              )}`}
            </Text.Bold>
          </Box>
        </Box>
      </Box>

      <Grid
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'right',
        }}
      >
        <Button
          label={translations.save}
          disabled={!hasChanges || loadingUpdate || loadingUsage}
          loading={loadingUpdate || loadingUsage}
          onClick={save}
        />
      </Grid>
    </>
  );
}

export { OrganisationUsage };
